import React, { Component } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';
import handleHttpError from '@/shared/services/HandleHttpError';

import classNames from 'classnames';
import moment from 'moment';
import { getRelationNameMapper } from '@/shared/components/RelationName';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import DataCacheService from '@/shared/services/DataCacheService';
import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { ToDoIcon } from '@/shared/components/Icons';
import ToastrService from '@/shared/services/ToastrService';
import api from '../../api';
import config from '../../../config';
import AutoComplete from './select/AutoComplete';
import dateFormatService from '../../services/DateFormatService';

const messages = defineMessages({
  Task: 'Task',
  NoTasksFound: 'No tasks found',
});
const taskSearch = (item, searchValue) => {
  if (!searchValue) return true;
  if (!item) return false;
  if (!item.search) return false;
  return item.search.toUpperCase().indexOf(searchValue.toUpperCase()) >= 0;
};
const renderItem = (item, searchValue) => (
  <span>
    {item.level > 0 && !item.isToDo && (
      <i
        className={classNames('fa', {
          'fa-circle greyed-out': item.status === 0,
          'fa-circle orange': item.status === 1,
          'fa-circle green': item.status === 2,
          'fa-minus-circle text-danger': item.status === 3,
        })}
      />
    )}
    {item.level > 0 && item.isToDo && (
      <ToDoIcon
        className={classNames({
          'greyed-out': item.status === 0,
          orange: item.status === 1,
          green: item.status === 2,
        })}
      />
    )}
    <span style={{ marginLeft: '3px' }} className={classNames({ 'margin-10L': item.level === 0 })}>
      {searchValue ? (
        <Highlighter
          highlightClassName="bold"
          searchWords={[searchValue]}
          textToHighlight={item.label}
        />
      ) : (
        item.label
      )}
    </span>
  </span>
);
class TasksForMultipleRelationsSelector extends Component {
  state = {
    items: [],
  };

  constructor(props) {
    super(props);
    this.nameMapper = getRelationNameMapper(props.intl);
    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
  }

  componentDidUpdate(prevProps) {
    const { initialSelectedItem, relations } = this.props;
    if (JSON.stringify(prevProps.initialSelectedItem) !== JSON.stringify(initialSelectedItem)) {
      this.setStateFromInitialItem(initialSelectedItem);
    }
    if (JSON.stringify(prevProps.relations) !== JSON.stringify(relations)) {
      this.retrieveTasks();
    }
  }

  getTaskLabel = task => {
    const { intl } = this.props;
    return (
      task.plannedTaskName ||
      (task.plannedTaskTemplateId
        ? intl.formatMessage(
            listItemMsg(
              'tasktemplate',
              task.plannedTaskTemplateId,
              task.plannedTaskTemplateName || '',
            ),
          )
        : '')
    );
  };

  componentDidMount() {
    this.retrieveTasks();
  }

  retrieveTasks = () => {
    const { initialSelectedItem, relations, intl } = this.props;
    if (!relations || relations.length === 0) {
      console.warn('No relations passed to TasksForMultipleRelationsSelector');
      return;
    }
    api.base
      .post(`${config.appUrls.tasks}/api/PlannedTasks/ForRelations`, {
        relations,
      })
      .then(
        ({ data }) => {
          if (!data || !data.length) {
            this.setState({
              items: [],
            });
            ToastrService.info(intl.formatMessage(messages.NoTasksFound));
            return;
          }
          const perStream = data.reduce((streams, task, mainTaskIndex) => {
            let stream = streams.find(x => x.invoiceStreamId === task.invoiceStreamId);
            if (!stream) {
              const relationName = this.nameMapper(task.relation);
              const streamName = this.assignmentsEnabled ? mapInvoiceStreamName(task, intl) : null;
              stream = {
                key: task.invoiceStreamId,
                invoiceStreamId: task.invoiceStreamId,
                relationId: task.relationId,
                relation: task.relation,
                relationName,
                streamName,
                label: streamName ? `${relationName} - ${streamName}` : relationName,
                level: 0,
                disabled: true,
                tasks: [],
              };
              streams.push(stream);
            }

            const deadline = task.deadline
              ? ` (${dateFormatService.formatDate(task.deadline)})`
              : '';
            const taskLabel = `${this.getTaskLabel(task)}${deadline}`;
            stream.tasks.push({
              ...task,
              label: taskLabel,
              id: task.plannedTaskId,
              key: task.plannedTaskId,
              search: `${taskLabel};${stream.label}`,
              order: mainTaskIndex,
              level: 1,
            });
            return streams;
          }, []);
          this.setState(
            {
              items:
                perStream.length === 1
                  ? perStream[0].tasks
                  : perStream.sort(dynamicSortMultiple('label')).reduce(
                      (result, stream) => {
                        const taskLabels = stream.tasks.map(x => x.label).join(';');
                        return result.concat([
                          {
                            ...stream,
                            search: `${stream.label};${taskLabels}`,
                          },
                          ...stream.tasks,
                        ]);
                      },

                      [],
                    ),
            },
            () => this.setStateFromInitialItem(initialSelectedItem),
          );
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };

  setStateFromInitialItem(initialSelectedItem) {
    const { tasks } = this.state;

    if (!initialSelectedItem) return;
    const task = tasks.find(x => x.plannedTaskId === initialSelectedItem);
    if (task) {
      this.setState({
        value: {
          ...task,
          key: task.plannedTaskId,
        },
      });
    }
  }

  onTaskChanged = task => {
    if (task === undefined) return;
    const { onSelect } = this.props;
    onSelect(task);
    this.setState({ value: task }, () => {
      this.setState({ value: null });
    });
  };

  render() {
    const { intl, disabled, emptyLabel, autoFocus } = this.props;
    const { value, items } = this.state;
    if (!items || !items.length) return null;
    return (
      <AutoComplete
        items={items}
        placeholder={intl.formatMessage(messages.Task)}
        onChange={item => this.onTaskChanged(item)}
        disabled={disabled}
        emptyLabel={emptyLabel}
        mode="edit"
        value={value}
        showAll
        filterFunc={taskSearch}
        renderItem={renderItem}
        autoFocus={autoFocus}
      />
    );
  }

  static propTypes = {
    intl: intlShape.isRequired,
    onSelect: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    emptyLabel: PropTypes.string,
    initialSelectedItem: PropTypes.string,
    relations: PropTypes.arrayOf(PropTypes.string).isRequired,
    autoFocus: PropTypes.bool,
  };

  static defaultProps = {
    disabled: false,
    emptyLabel: null,
    initialSelectedItem: null,
    autoFocus: false,
  };
}

export default injectIntl(TasksForMultipleRelationsSelector);
